<!--
  Created 09.10.2023
  Hero component for the proposal show page.
-->
<template>
  <section class="relative bg-gray-900 print:h-screen">
    <header class="container absolute inset-x-0 top-0 z-40 mx-auto p-6 md:py-12">
      <nav
        class="flex items-center justify-between"
        aria-label="Global">
        <div class="flex lg:flex-1">
          <div
            class="-m-1.5 p-1.5"
            :class="{'hover:cursor-pointer':contentEditable}"
            :title="(contentEditable
              ? 'Click to change branding in proposal settings'
              : (proposal.brand?.logoUrl
                ? proposal.brand.name + ' logo'
                : 'TravelOffice logo'
              )
            )"
            @click.prevent="() => ({contentEditable:changeBranding()})">
            <div v-if="proposal.brand">
              <span class="sr-only">{{ proposal.brand?.name }} logo</span>
              <img
                class="h-7 w-auto"
                :class="{
                  '!h-5' : proposal.data.brandSize === 'small',
                  '!h-7' : proposal.data.brandSize === 'medium',
                  '!h-10' : proposal.data.brandSize === 'large',
                  '!h-14' : proposal.data.brandSize === 'xl' }"
                :src="proposal.brand.logoUrl"
                :alt="proposal.brand.name + ' logo'">
            </div>
            <div v-else>
              <span class="sr-only">TravelOffice logo</span>
              <img
                class="h-7 w-auto"
                src="/img/traveloffice-logo-white.svg"
                alt="TravelOffice">
            </div>
          </div>
        </div>

        <!-- Menu hamburger icon -->
        <div
          v-if="!contentEditable"
          class="flex print:hidden lg:hidden">
          <button
            type="button"
            class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-400"
            @click="mobileMenuOpen = true">
            <span class="sr-only">Open main menu</span>
            <Bars3Icon
              class="h-6 w-6"
              aria-hidden="true" />
          </button>
        </div>
      </nav>

      <!-- Mobile menu -->
      <Dialog
        v-if="!contentEditable"
        as="div"
        class="print:hidden lg:hidden"
        :open="mobileMenuOpen"
        @close="mobileMenuOpen = false">
        <div class="fixed inset-0 z-40" />
        <DialogPanel class="fixed inset-y-0 right-0 z-40 w-full overflow-y-auto bg-gray-900 p-6 sm:max-w-sm sm:ring-1 sm:ring-white/10">
          <div class="flex items-center justify-between">
            <a
              href="#"
              class="-m-1.5 p-1.5">
              <span class="sr-only">TravelOffice</span>
              <img
                class="h-5 w-auto"
                src="/img/traveloffice-logo-white.svg"
                alt="TravelOffice">
            </a>
            <button
              type="button"
              class="-m-2.5 rounded-md p-2.5 text-gray-400"
              @click="mobileMenuOpen = false">
              <span class="sr-only">Close menu</span>
              <XMarkIcon
                class="h-6 w-6"
                aria-hidden="true" />
            </button>
          </div>
          <div class="mt-6 flow-root">
            <div class="-my-6 divide-y divide-gray-500/25">
              <div class="space-y-2 py-6">
                <a
                  v-for="item in navItems"
                  :key="item.title"
                  :href="item.component"
                  class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-white hover:bg-gray-800"
                  @click.prevent="mobileNavClick(item)">{{ item.title }}</a>
              </div>
              <div class="py-6">
                <a
                  href="#"
                  class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-500 hover:bg-gray-800">About TravelOffice</a>
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>

    <!-- Hero image, title and subtitle -->
    <div
      class="group relative isolate h-screen overflow-hidden max-sm:h-[100svh] md:h-screen lg:max-h-[900px]"
      :class="{'max-sm:h-[calc(100svh-4rem)] md:h-[calc(100vh-4rem)] lg:max-h-[calc(900px-4rem)]':contentEditable}">
      <!-- Hero image -->

      <MediaFigure
        :image="image"
        :image-alt="image?.description ?? ''"
        image-overlay />

      <!-- Image editing -->
      <div
        v-if="contentEditable"
        class="absolute inset-0 mt-60 w-full p-4 pb-24">
        <div class="mx-auto max-w-7xl">
          <media-select-single
            :selected="image"
            :settings="{
              proposal: {
                name: 'Current Proposal',
                get: route('api.proposal.media.index', {proposal: proposal.id}),
                upload: route('api.proposal.media.store', {proposal: proposal.id})
              },
              destination: {
                name: proposal.destinations[0]?.name ?? 'Original destination',
                get: route('api.destination.entity.media.index', {entity: proposal.destinations[0]?.id}),
                upload: route('api.destination.entity.media.store', {entity: proposal.destinations[0]?.id})
              }
            }"
            :default_search="proposal.destinations[0] ? proposal.destinations[0]?.name + ', ' + proposal.destinations[0]?.parent?.name : ''"
            :cropable="typeof image !== 'string'"
            :coordinates="imageCrop ? imageCrop.coordinates : null"
            hide-media-update-buttons
            @update="updateMedia"
            @crop="cropped" />
        </div>
      </div>

      <!-- Hero content -->
      <div class="absolute inset-x-0 bottom-0">
        <div class="container mx-auto flex flex-col px-6 py-12">
          <!-- Title -->
          <TextEditableTextarea
            v-if="contentEditable"
            id="proposal-title"
            v-model="proposal.title"
            background-style="black"
            :text-area-class="'bg-transparent text-4xl font-bold tracking-tight text-white md:text-6xl lg:text-7xl ' +
              'placeholder:italic placeholder:text-white placeholder:opacity-50 cursor-text'"
            :placeholder="'Write your proposal title here..'"
            :show-buttons="false"
            :saved-changes="savedChanges"
            @edited="store.unsavedChanges.changes.proposal = true;" />
          <h1
            v-else
            class="whitespace-pre-line text-4xl font-bold tracking-tight text-white text-shadow-sm md:text-6xl lg:text-7xl">
            {{ proposal.title }}
          </h1>
          <!-- Subtitle -->
          <div
            class="mt-3 flex flex-col space-y-8 whitespace-pre-line text-shadow-sm
              lg:mt-3 lg:flex-row lg:items-end lg:justify-between lg:space-y-0">
            <TextEditableTextarea
              v-if="contentEditable"
              id="proposal-subtitle"
              v-model="proposal.subtitle"
              class="basis-1/2"
              background-style="black"
              :text-area-class="'bg-transparent text-lg font-medium leading-8 text-gray-100 lg:text-xl whitespace-pre-line' +
                'placeholder:italic placeholder:text-white placeholder:opacity-50 cursor-text'"
              :placeholder="'Write your proposal subtitle here..'"
              :show-buttons="false"
              :saved-changes="savedChanges"
              @edited="store.unsavedChanges.changes.proposal = true;" />
            <p
              v-else
              class="basis-1/2 whitespace-pre-line text-lg font-medium leading-8 text-gray-100 lg:text-xl">
              {{ proposal.subtitle }}
            </p>
            <!-- Trip company and date -->
            <div class="whitespace-pre-line text-lg font-medium leading-8 text-gray-100 lg:text-xl">
              <TextEditableTextarea
                v-if="contentEditable"
                id="proposal-sideTitle"
                v-model="proposal.data.sideTitle"
                class="basis-1/2"
                background-style="black"
                :text-area-class="'bg-transparent text-lg font-medium leading-8 text-gray-100 lg:text-xl whitespace-pre-line' +
                  'placeholder:italic placeholder:text-white placeholder:opacity-50 cursor-text text-right'"
                editing-indicator-class="ml-[2px]"
                :placeholder="'Write your side-title here..'"
                :show-buttons="false"
                :saved-changes="savedChanges"
                @edited="store.unsavedChanges.changes.proposal = true;" />
              <p
                v-else
                class="whitespace-pre-line text-lg font-medium leading-8 text-gray-100 lg:text-xl">
                {{ proposal.data.sideTitle }}
              </p>
            </div>
          </div>
          <!-- Navbar -->
          <ProposalNavBar
            :nav-items="navItems"
            class="mt-12 hidden h-24 print:!hidden lg:block"
            @scroll-into-view="(v) => $emit('scrollIntoView', { component: v.component.toString() })" />
          <div class="mt-12 flex w-full animate-bounce justify-center print:hidden lg:hidden">
            <ChevronDoubleDownIcon
              class="h-6 w-6 cursor-pointer text-white"
              @mouseup="$emit('scrollIntoView', { component: 'ProposalIntroComponent' })" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
/* external */
import {
  computed, onMounted,
  ref,
} from 'vue';
import {
  Dialog, DialogPanel,
} from '@headlessui/vue';
import {
  Bars3Icon, XMarkIcon, ChevronDoubleDownIcon,
} from '@heroicons/vue/24/outline';
/* local */
import ProposalNavBar from '@/Pages/Proposal/Components/ProposalNavBar.vue';
import { useProposalStore } from '@/Stores/Proposal';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import MediaSelectSingle from '@/Components/Media/MediaPickerSingle.vue';
import MediaFigure from '@/Pages/Proposal/Components/MediaFigure.vue';
import moment from 'moment-timezone';
import { isSameMonth } from '@/utils';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const emit = defineEmits(['scrollIntoView', 'showProposalSettings']);

const props = defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
});

const store = useProposalStore();

const proposal = computed(() => store.proposal);
const navItems = computed(() => store.navItems);
const savedChanges = computed(() => !store.unsavedChanges.changes.proposal);
const image = computed(() => proposal.value.image ?? null);
const imageCrop = computed(() => proposal.value.media_crop ?? null);

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

const mobileMenuOpen = ref(false);
const mobileNavClick = (item) => {
  mobileMenuOpen.value = false;
  setTimeout(() => {
    emit('scrollIntoView', { component: item.component.toString() });
  }, 100);
};

/**
 * Change branding
 * @returns {boolean} - true if contentEditable
 */
function changeBranding() {
  if (!props.contentEditable) {
    return false;
  }

  emit('showProposalSettings', true);

  return true;
}

/**
 * Update media function for media picker
 * @param {object} media - Media object
 */
function updateMedia(media) {
  proposal.value.image = media;
  proposal.value.media_id = media.id;
  store.unsavedChanges.changes.proposal = true;
}
/**
 * Update media crop function for media picker
 * @param {object} data - Media crop data
 */
function cropped(data) {
  proposal.value.media_crop = data;
  store.unsavedChanges.changes.proposal = true;
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onMounted(() => {
  if (proposal.value.data?.sideTitle === null) {
    let dateFormatted = '';
    if (isSameMonth(proposal.value.start_date, proposal.value.end_date)) {
      dateFormatted = moment(proposal.value.start_date).format('D-')
        + moment(proposal.value.end_date).format('LL');
    } else {
      dateFormatted = `${moment(proposal.value.start_date).format('D. MMM')
      } - ${moment(proposal.value.end_date).format('D. MMM YYYY')}`;
    }

    proposal.value.data.sideTitle = `Tur for ${proposal.value.client.name}, ${dateFormatted}`;
  }
});

</script>
